<script setup lang="ts">
    import type { ProductListData } from '~/@types/cms';
    import type { IWidgetProps } from '~/composables/widgets';
    import type { ProductPreviewFieldsFragment } from '~/graphql/generated';

    const appStore = useAppStore();

    /**
     * Products Widget Container
     * loads a collection via the api and renders a set of cards for each product
     * while loading the collection, a skeleton should be rendered
     * we only fetch the first collection in the array
     */
    const props = defineProps<IWidgetProps<ProductListData>>();
    const { data } = useWidget(props);
    const { locale, country, language } = useLocales();
    const { queryParams } = usePreview();
    const collectionId = `${country.value}:${language.value}:collection:${data.handle}`;
    const { data: collectionData, status: collectionStatus } = await useProductCollection(
        collectionId,
        Boolean(data.type === 'collection' && data.handle),
    );

    const { data: recommendedData, status: recommendedStatus } = await useAsyncData<ProductPreviewFieldsFragment[]>(
        `${appStore.pageCache?.page?.product?.nativeId ?? 'unknown'}-recommended`,
        async () => {
            if (data.type !== 'recommended' || !appStore.pageCache?.page?.product?.nativeId) {
                return [];
            }
            try {
                const response = await $fetch('/api/related', {
                    query: {
                        id: appStore.pageCache.page.product.nativeId,
                        locale: locale.value,
                        ...queryParams.value,
                    },
                });
                return (response?.data?.recommendedProducts ?? []) as ProductPreviewFieldsFragment[];
            } catch (error) {
                console.error('Error fetching related products:', error);
                return [];
            }
        },
    );

    const products = computed(() => (data.type === 'collection' ? collectionData.value : recommendedData.value));
    const status = computed(() => (data.type === 'collection' ? collectionStatus.value : recommendedStatus.value));

    const background = computed(() => {
        return data.style?.background_color;
    });
</script>
<template>
    <!-- Empty div fixes the hydration issue -->
    <template v-if="status === 'success' && Array.isArray(products)">
        <org-product-grid
            v-if="data.render === 'grid'"
            :products="products"
            :background="background"
            :intro="data.intro"
            :color-filter="data.filter"
            class="cms-product-list" />
        <org-product-slider
            v-else
            :products="products"
            :background="background"
            :intro="data.intro"
            :color-filter="data.filter"
            class="cms-product-list" />
    </template>
    <div v-else></div>
</template>
